import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { UserPlus, Smartphone, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import {
  isIndicacaoHostname,
  portalflyUrl,
  redirectIndicacaoHostToPortalfly,
} from '@/utils/indicacaoHost';

const cadastroSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  telefone: z
    .string()
    .regex(/^\d+$/, 'Informe apenas números')
    .min(10, 'Informe um telefone válido (com DDD)')
    .max(15, 'Telefone muito longo'),
});

type CadastroFormData = z.infer<typeof cadastroSchema>;

/**
 * Página pública do link de indicação.
 * URL: /indicacao?ref=xxx (ref = id do usuário que indicou). Produção: portalfly.zehmotoca.com.br/indicacao
 * Formulário: apenas nome e telefone do indicado; registra a indicação sem criar conta.
 */
export function IndicacaoPage() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') ?? '';
  const hasRef = ref.length > 0;
  const [cadastroSuccess, setCadastroSuccess] = useState(false);

  const appDeepLink = hasRef ? `zehmotoca://indicacao?ref=${encodeURIComponent(ref)}` : null;

  const loginHref =
    typeof window !== 'undefined' && isIndicacaoHostname(window.location.hostname)
      ? portalflyUrl('/login')
      : '/login';

  useEffect(() => {
    redirectIndicacaoHostToPortalfly();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CadastroFormData>({
    resolver: zodResolver(cadastroSchema),
  });

  const telefoneField = register('telefone');

  const onSubmit = async (data: CadastroFormData) => {
    if (!hasRef) return;
    try {
      await api.post('/mobile/indicacao/register', {
        ref,
        nome: data.nome.trim(),
        telefone: data.telefone.replace(/\D/g, ''),
      });
      setCadastroSuccess(true);
      toast.success('Indicação registrada com sucesso!');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao registrar. Tente novamente.';
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-6 w-6 text-primary" aria-hidden />
              <h1 className="text-lg font-semibold text-foreground">Indicação Zeh Motoca</h1>
            </div>
            <a href={loginHref}>
              <Button variant="outline" size="sm" className="gap-1" type="button">
                <ArrowLeft className="h-4 w-4" />
                Voltar ao login
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Smartphone className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-center text-foreground mb-3">
            Você foi indicado(a) para o app Zeh Motoca
          </h2>
          <p className="text-muted-foreground text-center leading-relaxed mb-6">
            Informe seu nome e telefone para registrar a indicação. Quem te indicou poderá acompanhar.
          </p>

          {!hasRef && (
            <p className="text-sm text-muted-foreground text-center">
              Link de indicação inválido (parâmetro ref ausente). Peça um novo link a quem te indicou.
            </p>
          )}

          {hasRef && !cadastroSuccess && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="Nome completo"
                placeholder="Seu nome"
                {...register('nome')}
                error={errors.nome?.message}
                required
                autoComplete="name"
              />
              <Input
                label="Telefone (com DDD)"
                type="tel"
                inputMode="numeric"
                placeholder="(00) 00000-0000"
                maxLength={15}
                {...telefoneField}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, '');
                  telefoneField.onChange(e);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const digits = e.clipboardData.getData('text').replace(/\D/g, '');
                  e.currentTarget.value = digits.slice(0, 15);
                  telefoneField.onChange({
                    ...e,
                    target: e.currentTarget,
                    type: 'change',
                  });
                }}
                error={errors.telefone?.message}
                required
                autoComplete="tel"
              />
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Registrando...' : 'Registrar indicação'}
              </Button>
            </form>
          )}

          {hasRef && cadastroSuccess && (
            <div className="space-y-4 text-center">
              <p className="text-foreground font-medium">
                Indicação registrada com sucesso!
              </p>
              <p className="text-sm text-muted-foreground">
                Quem te indicou foi avisado. Baixe o app Zeh Motoca para acessar seus serviços.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <a href={loginHref}>
                  <Button type="button" variant="default" className="w-full sm:w-auto">
                    Ir para o login
                  </Button>
                </a>
                {appDeepLink && (
                  <a href={appDeepLink}>
                    <Button type="button" variant="outline" className="w-full sm:w-auto gap-2">
                      <Smartphone className="h-4 w-4" />
                      Abrir no app
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}

          {hasRef && !cadastroSuccess && (
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground text-center mb-2">
                Já tem o app instalado?
              </p>
              {appDeepLink && (
                <a
                  href={appDeepLink}
                  className="inline-flex items-center justify-center gap-2 w-full rounded-lg border border-primary bg-primary/10 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
                >
                  <Smartphone className="h-4 w-4" />
                  Abrir no app
                </a>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
